import React, { useState, useEffect, useRef } from "react";
import { DataTable, DataTableSortEvent, DataTableStateEvent, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { CustomerFollowup } from "../../models/orders/order-list-model";
import editUserServices from "../../services/edit-users/edit-users.service";
import { Toast } from 'primereact/toast';
 
interface DataTableProps {
    data: CustomerFollowup[];
    pageNumber: number;
    pageSize: number;
    totalRecords: number;
    onPageChange: (event: DataTableStateEvent) => void;
    refreshData: () => Promise<void>;
    onSortChange?: (field: string, direction: string) => void;
}
 
interface CallStatus {
    callStatusId: number;
    callStatusName: string;
}
 
const CustomerFollowUpDatatable: React.FC<DataTableProps> = (props) => {
    const [data, setData] = useState<CustomerFollowup[]>([]);
    const [sortField, setSortField] = useState<string>(' ');
    const [sortOrder, setSortOrder] = useState<1 | -1>(1);
    const [monthColumns, setMonthColumns] = useState<string[]>([]);
    const [editingRow, setEditingRow] = useState<number | null>(null);
    const [callStatusOptions, setCallStatusOptions] = useState<{ label: string, value: number}[]>([]);
    const [loadingCallStatuses, setLoadingCallStatuses] = useState<boolean>(false);
    const [editingValues, setEditingValues] = useState<Record<number, number | null>>({});
    const toast = useRef<Toast>(null);
 
    useEffect(() => {
        if (props.data.length > 0 && props.data[0].monthlyCallStatuses) {
            const orderedMonths = Object.keys(props.data[0].monthlyCallStatuses);
            setMonthColumns(orderedMonths);
        }
        setData(props.data);
    }, [props.data]);
 
    useEffect(() => {
        const fetchCallStatuses = async () => {
            setLoadingCallStatuses(true);
            try {
                const callStatuses = await editUserServices.getCallStatuses();
                if (callStatuses && callStatuses.length > 0) {
                    const formattedStatuses = callStatuses.map((status: CallStatus) => ({
                        label: status.callStatusName,
                        value: status.callStatusId
                    }));
                    setCallStatusOptions(formattedStatuses);
                }
            } catch (error) {
                console.error('Error fetching call statuses:', error);
                setCallStatusOptions([]);
                showToast('error', 'Error', 'Failed to load call status options');
            } finally {
                setLoadingCallStatuses(false);
            }
        };
 
        fetchCallStatuses();
    }, []);
 
    const showToast = (severity: 'success' | 'error', summary: string, detail: string) => {
        toast.current?.show({ severity, summary, detail, life: 3000 });
    };
 
    const onSort = (e: DataTableSortEvent) => {
        const field = e.sortField;
        const direction = e.sortOrder === 1 ? "asc" : "desc";
 
        setSortField(field);
        setSortOrder(e.sortOrder as 1 | -1);
 
        if (props.onSortChange) {
    props.onSortChange(field, direction);
}
    };
 
    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, "0");
        const month = date.toLocaleString("en-GB", { month: "long" });
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };
 
    const formatCallStatus = (status: string | null) => {
        if (!status) return '-';
        const statusObj = callStatusOptions.find(opt => opt.value.toString() === status);
        return statusObj ? statusObj.label : status;
    };
 
    const onRowEditComplete = async (e: DataTableRowEditCompleteEvent) => {
        const rowData = e.newData as CustomerFollowup;
        const currentMonth = monthColumns[0];
        const newStatusValue = editingValues[rowData.userID];
 
        if (newStatusValue !== undefined && newStatusValue !== null) {
            await handleStatusChange(rowData.userID, newStatusValue);
        }
        setEditingRow(null);
    };
 
    const handleStatusChange = async (userId: number, newStatusValue: number) => {
        if (newStatusValue === null) return;
 
        const currentMonth = monthColumns[0];
        try {
            const callHistoryData = {
                customerId: userId,
                callStatusId: newStatusValue
            };
 
            const result = await editUserServices.createCallHistory(callHistoryData);
 
            if (result.isSuccess) {
                const updatedData = data.map(item => {
                    if (item.userID === userId) {
                        return {
                            ...item,
                            monthlyCallStatuses: {
                                ...item.monthlyCallStatuses,
                                [currentMonth]: newStatusValue.toString()
                            }
                        };
                    }
                    return item;
                });
 
                setData(updatedData);
                showToast('success', 'Success', 'Call status updated successfully');
                await props.refreshData();
            } else {
                showToast('error', 'Error', 'Failed to update call status');
            }
        } catch (error) {
            console.error('Error saving call history:', error);
            showToast('error', 'Error', 'Failed to update call status');
        }
    };
 
    const callStatusEditor = (options: any) => {
        const currentMonth = monthColumns[0];
        const rowData = options.rowData;
        const currentStatus = rowData.monthlyCallStatuses?.[currentMonth] || null;
        const currentValue = currentStatus ? parseInt(currentStatus) : null;
 
        return (
            <Dropdown
                value={editingValues[rowData.userID] !== undefined ? editingValues[rowData.userID] : currentValue}
                options={callStatusOptions}
                optionLabel="label"
                optionValue="value"
                onChange={(e: DropdownChangeEvent) => {
                    setEditingValues({
                        ...editingValues,
                        [rowData.userID]: e.value
                    });
                }}
                disabled={loadingCallStatuses}
                style={{ width: '100%' }}
            />
        );
    };
 
    return (
        <div className="col-12">
            <Toast ref={toast} />
            <div className="card border-noround p-2">
                <DataTable
                    value={data}
                    paginator
                    rows={props.pageSize}
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    totalRecords={props.totalRecords}
                    lazy
                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                    currentPageReportTemplate={'{first} to {last} of {totalRecords}'}
                    first={(props.pageNumber - 1) * props.pageSize}
                    onPage={props.onPageChange}
                    sortMode="single"
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={onSort}
                    editMode="row"
                    onRowEditComplete={onRowEditComplete}
                >
                    <Column field="customerName" header="Customer Name" sortable/>
                    <Column field="primaryContactNbr" header="Contact Number" sortable />
                    <Column field="recentOrder" header="Recent Order" body={(rowData) => formatDate(rowData.recentOrder)} sortable/>
                    <Column field="daysSinceRecentOrder" header="Days Since Last Ordered" sortable />
                    <Column field="recentOrderAmount" header="Last Order Amount" sortable />
                    <Column field="routeCodeName" header="Route" sortable />
                    <Column field="lastCalled" header="Last Called" body={(rowData) => formatDate(rowData.lastCalled)} sortable/>
 
                    {monthColumns.map((month, index) => {
                        if (index === 0) {
                            return (
                                <Column
                                    key={month}
                                    header={month}
                                    body={(rowData) => formatCallStatus(rowData.monthlyCallStatuses?.[month])}
                                    editor={(options) => callStatusEditor(options)}
                                />
                            );
                        } else {
                            return (
                                <Column
                                    key={month}
                                    header={month}
                                    body={(rowData) => formatCallStatus(rowData.monthlyCallStatuses?.[month])}
                                />
                            );
                        }
                    })}
 
                    <Column
                        rowEditor
                        header="Action"
                        headerStyle={{ width: '10%', minWidth: '8rem' }}
                        bodyStyle={{ textAlign: 'center' }}
                    />
                </DataTable>
            </div>
        </div>
    );
};
 
export default CustomerFollowUpDatatable;